import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { isManagedReadOnly, isManagedBotHidden } from '../utils/whatsappLifecycle';
import { subscribeInboxChanges, mergeOpenedSession, startInboxRecovery } from '../utils/whatsappInboxSync';
import { startWhatsAppReminders } from '../utils/whatsappReminders';
import { createPortal } from 'react-dom';
import { MessageSquare, Search, Loader2, AlertCircle, Smile, FileText, Send, Plus, Phone, X, Paperclip, Lock, History, Mail, Bell, ShoppingCart, Calendar, CheckCircle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Archive, Contact, SlidersHorizontal, Clock, AlarmClock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../services/supabaseClient';
import { useTramite } from '../context/TramiteContext';
import { useSalesData } from '../context/SalesDataContext';
import { useCapabilities } from '../hooks/useCapabilities';
import { useAppendix } from '../context/AppendixContext';
import WhatsAppTemplateButton from './WhatsAppTemplateButton';
import WhatsAppAttachments from './WhatsAppAttachments';
import TagDisplayList from './common/TagDisplayList';
import CopyablePhone from './CopyablePhone';

// Icon mapping for dynamic template icons
const LucideIcons = {
    FileText,
    Smile,
    MessageSquare,
    Mail,
    Bell,
    ShoppingCart,
    Calendar,
    CheckCircle,
    Send,
    Phone
};

const getChannelLabel = (channel) => {
    const customName = typeof channel.custom_name === 'object'
        ? channel.custom_name?.name
        : channel.custom_name;

    return customName || channel.display_phone_number || channel.id;
};

const WhatsAppInboxSidebar = ({ onOpenConversation, onStolen, activeConversationId, onOpenHistoricalConversation, onOpenCallTrace, isExpanded = false, onToggleExpand }) => {
    const { authFetch, agentInfo, activeRole } = useAuth();

    // Active channel tab within the current role ('all' or a channel id)
    const { theme } = useTheme();
    const { currentTramite, setCurrentTramite } = useTramite();
    const { 
        updateSectionData, 
        fetchLeadByPhone,
        extractedData,
        analyzedAttachments
    } = useSalesData();
    const { appendices } = useAppendix();

    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(false);
    
    const conversationsRef = useRef([]);
    useEffect(() => {
        conversationsRef.current = conversations;
    }, [conversations]);

    const activeConversationIdRef = useRef(activeConversationId);
    useEffect(() => {
        activeConversationIdRef.current = activeConversationId;
    }, [activeConversationId]);

    // Track ongoing refresh attempts to prevent duplicate concurrent refreshes
    const refreshInProgressRef = useRef(new Map());
    const refreshPendingRef = useRef(new Map());
    const refreshDebounceTimersRef = useRef(new Map());
    const recentlyInsertedConversationsRef = useRef(new Map());
    const inboxMissesRef = useRef(new Set());
    const inboxRevisionRef = useRef(0);
    const sessionRequestRef = useRef(0);

    console.log('[WA Inbox Sidebar] Component mounted/rendered');
    console.log('[WA Inbox Sidebar] Current state:', { conversationsCount: conversations.length, loading, agentInfo: agentInfo?.email });
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    
    // Toggle para filtrar solo conversaciones inbound sin respuesta (chip rápido junto al buscador)
    const [showInboundUnanswered, setShowInboundUnanswered] = useState(() => {
        try {
            const saved = localStorage.getItem('cti_whatsapp_show_inbound_unanswered');
            return saved === 'true';
        } catch (e) {
            return false;
        }
    });
    
    // Sincronizar showInboundUnanswered con localStorage
    useEffect(() => {
        localStorage.setItem('cti_whatsapp_show_inbound_unanswered', showInboundUnanswered);
    }, [showInboundUnanswered]);

    // ---- Panel de filtros enriquecido ----
    const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

    // Asignación: 'all' | 'mine' | 'unassigned' | 'others'
    const [assignedFilter, setAssignedFilter] = useState(() => {
        try {
            return localStorage.getItem('cti_whatsapp_filter_assigned') || 'all';
        } catch (e) {
            return 'all';
        }
    });
    useEffect(() => {
        localStorage.setItem('cti_whatsapp_filter_assigned', assignedFilter);
    }, [assignedFilter]);

    // Gestión: 'true' | 'false' | 'all'
    const [enGestionFilter, setEnGestionFilter] = useState(() => {
        try {
            return localStorage.getItem('cti_whatsapp_filter_en_gestion') || 'true';
        } catch (e) {
            return 'true';
        }
    });
    useEffect(() => {
        localStorage.setItem('cti_whatsapp_filter_en_gestion', enGestionFilter);
    }, [enGestionFilter]);

    // Ventana de servicio: set de 'valid' | 'expiring_soon' | 'expired'
    const [serviceWindowFilter, setServiceWindowFilter] = useState(() => {
        try {
            const saved = localStorage.getItem('cti_whatsapp_filter_service_window');
            return saved ? saved.split(',').filter(Boolean) : [];
        } catch (e) {
            return [];
        }
    });
    useEffect(() => {
        localStorage.setItem('cti_whatsapp_filter_service_window', serviceWindowFilter.join(','));
    }, [serviceWindowFilter]);

    // Gestión diferida/inmediata: set de 'diferida' | 'inmediata' (independiente de en_gestion)
    const [managementStateFilter, setManagementStateFilter] = useState(() => {
        try {
            const saved = localStorage.getItem('cti_whatsapp_filter_management_state');
            return saved ? saved.split(',').filter(Boolean) : [];
        } catch (e) {
            return [];
        }
    });
    useEffect(() => {
        localStorage.setItem('cti_whatsapp_filter_management_state', managementStateFilter.join(','));
    }, [managementStateFilter]);

    // Orden: 'recent' | 'urgency' | 'oldest'
    const [sortBy, setSortBy] = useState(() => {
        try {
            return localStorage.getItem('cti_whatsapp_sort_by') || 'recent';
        } catch (e) {
            return 'recent';
        }
    });
    useEffect(() => {
        localStorage.setItem('cti_whatsapp_sort_by', sortBy);
    }, [sortBy]);

    // Rango de fechas de última interacción del cliente
    const [dateRange, setDateRange] = useState({ from: '', to: '' });

    const toggleServiceWindowOption = (option) => {
        setServiceWindowFilter(prev =>
            prev.includes(option) ? prev.filter(o => o !== option) : [...prev, option]
        );
    };

    const toggleManagementStateOption = (option) => {
        setManagementStateFilter(prev =>
            prev.includes(option) ? prev.filter(o => o !== option) : [...prev, option]
        );
    };

    // Acceso rápido: alterna un listado que muestra ÚNICAMENTE las conversaciones diferidas
    const isDeferredOnlyView = managementStateFilter.length === 1 && managementStateFilter[0] === 'diferida';
    const toggleDeferredOnlyView = () => {
        setManagementStateFilter(prev => (
            prev.length === 1 && prev[0] === 'diferida' ? [] : ['diferida']
        ));
    };

    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (assignedFilter !== 'all') count++;
        if (enGestionFilter !== 'true') count++; // 'true' es el valor por defecto
        if (serviceWindowFilter.length > 0) count++;
        if (managementStateFilter.length > 0) count++;
        if (sortBy !== 'recent') count++;
        if (dateRange.from || dateRange.to) count++;
        return count;
    }, [assignedFilter, enGestionFilter, serviceWindowFilter, managementStateFilter, sortBy, dateRange]);

    const clearAllFilters = () => {
        setAssignedFilter('all');
        setEnGestionFilter('true');
        setServiceWindowFilter([]);
        setManagementStateFilter([]);
        setSortBy('recent');
        setDateRange({ from: '', to: '' });
    };

    const [isNewConvModalOpen, setIsNewConvModalOpen] = useState(false);
    const [newConvPhone, setNewConvPhone] = useState('');
    const [newConvChannelId, setNewConvChannelId] = useState(''); // Selected channel for new conversation
    const [availableTemplates, setAvailableTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    
    // Auto-select channel when role has exactly 1 channel or modal opens
    useEffect(() => {
        if (isNewConvModalOpen && activeRole?.channels) {
            if (activeRole.channels.length === 1) {
                setNewConvChannelId(activeRole.channels[0].id);
            } else if (activeRole.channels.length === 0) {
                setNewConvChannelId('');
            }
        }
    }, [activeRole, isNewConvModalOpen]);
    const PAGE_SIZE = 10;

    const { hasCapability } = useCapabilities();
    const canTrace = hasCapability('calltrace');

    const [selectedTags, setSelectedTags] = useState([]);
    const refreshRelevantFiltersRef = useRef({});
    refreshRelevantFiltersRef.current = {
        messagesCanChangeFilters: showInboundUnanswered || Boolean(dateRange.from || dateRange.to),
        tagsCanChangeFilters: selectedTags.length > 0,
    };

    useEffect(() => {
        inboxMissesRef.current.clear();
    }, [activeRole?.id, assignedFilter, searchTerm, selectedTags, showInboundUnanswered, enGestionFilter, serviceWindowFilter, managementStateFilter, dateRange]);
    
    // Compute available tags from loaded conversations
    const availableTags = useMemo(() => {
        const tagMap = new Map();
        conversations.forEach(conv => {
            if (conv.tags && Array.isArray(conv.tags)) {
                conv.tags.forEach(tag => {
                    if (tag && tag.id && !tagMap.has(tag.id)) {
                        tagMap.set(tag.id, tag);
                    }
                });
            }
        });
        return Array.from(tagMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [conversations]);

    // Título de la pestaña con recuento de pendientes
    useEffect(() => {
        const unreadCount = conversations.filter(c => c.attention_required && !isManagedReadOnly(c)).length;
        if (unreadCount > 0) {
            document.title = `(${unreadCount}) Consola Agente`;
        } else {
            document.title = 'Consola Agente';
        }

        return () => {
            // Limpia el título si el componente se desmonta (ej. cambia de capability)
            document.title = 'Consola Agente';
        };
    }, [conversations]);

    const triggerNotification = useCallback((title = 'Bandeja Compartida', body = 'Tienes mensajes pendientes de atender') => {
        if ('Notification' in window && Notification.permission === 'granted') {
            try {
                new Notification(title, { body, icon: '/favicon.ico' });
                return true;
            } catch (err) {
                console.warn('[WA Inbox] Could not show notification:', err);
            }
        }
        return false;
    }, []);

    // Personal reminders survive searches, pagination and role changes. The
    // server also returns overdue reminders when reopening/reloading the console.
    const refreshRemindersRef = useRef(() => {});
    useEffect(() => {
        if (!agentInfo?.id) return;
        const reminders = startWhatsAppReminders({
            authFetch,
            agentId: agentInfo.id,
            notify: conv => triggerNotification(
                'Gestión inmediata',
                `La conversación con ${conv.contact_name || conv.phone_number || 'un contacto'} requiere atención ahora`
            ),
            onDue: conv => setConversations(prev => {
                if (!prev.some(c => c.conversation_id === conv.conversation_id && c.management_state === 'diferida')) return prev;
                return prev.map(c => c.conversation_id === conv.conversation_id
                    ? { ...c, management_state: 'inmediata' } : c);
            }),
        });
        refreshRemindersRef.current = reminders.refresh;
        return () => {
            reminders.stop();
            refreshRemindersRef.current = () => {};
        };
    }, [authFetch, agentInfo?.id, triggerNotification]);

    // Attachments Decoupling
    const [waAttachments, setWaAttachments] = useState([]);
    const [waStatus, setWaStatus] = useState('idle');
    const [isAttachmentsOpen, setIsAttachmentsOpen] = useState(false);
    const [attachmentsConvId, setAttachmentsConvId] = useState(null);

    const fetchConversationAttachments = useCallback(async (convId, silent = false) => {
        try {
            if (!silent) setWaStatus('fetching');
            const response = await authFetch(`/api/whatsapp/attachments/conversation/${convId}`);
            if (response.ok) {
                const data = await response.json();
                if (Array.isArray(data)) {
                    setWaAttachments(data);
                }
                if (!silent) setWaStatus('completed');
            } else {
                if (!silent) setWaStatus('idle');
            }
        } catch (err) {
            console.error("[WA Inbox Sidebar] Error fetching attachments:", err);
            if (!silent) setWaStatus('idle');
        }
    }, [authFetch]);

    // Auto-fetch attachments when selected conversation changes
    useEffect(() => {
        if (attachmentsConvId && isAttachmentsOpen) {
            fetchConversationAttachments(attachmentsConvId);
        } else if (!isAttachmentsOpen) {
            // Option to clear if closed
        }
    }, [attachmentsConvId, isAttachmentsOpen, fetchConversationAttachments]);

    // Anti-Blink Realtime Sync for specific conversation attachments
    useEffect(() => {
        if (!attachmentsConvId || !isAttachmentsOpen) return;

        const channelName = `wa-attachments-sync-${attachmentsConvId}`;
        const channel = supabase
            .channel(channelName)
            .on('postgres_changes', { 
                event: 'INSERT', 
                schema: 'messaging', 
                table: 'attachments'
            }, (dbPayload) => {
                console.log(`[WA Inbox Sidebar] 🔔 Nuevo archivo procesado. Comprobando actualización para ${attachmentsConvId}...`);
                setTimeout(() => {
                    fetchConversationAttachments(attachmentsConvId, true);
                }, 300); // 300ms for db transaction
            })
            .subscribe((status) => {
                console.log(`[WA Sync 2.0] Attachments Realtime Status for ${attachmentsConvId}:`, status);
            });

        return () => {
            supabase.removeChannel(channel);
        };
    }, [attachmentsConvId, isAttachmentsOpen, fetchConversationAttachments]);

    const handleDownloadAttachment = async (att) => {
        try {
            const url = `/api/whatsapp/download/${att.id}`;
            const response = await authFetch(url);
            if (!response.ok) throw new Error('Download failed');
            
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            
            const disposition = response.headers.get('content-disposition');
            let fileName = `whatsapp_file_${att.id}`;
            
            if (disposition) {
                const fileNameMatch = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (fileNameMatch && fileNameMatch[1]) {
                    fileName = fileNameMatch[1].replace(/['"]/g, '');
                }
            } else if (att.name) {
                fileName = att.name;
            }

            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('[WA Inbox Sidebar] Error downloading attachment:', error);
            alert('Error al descargar el archivo');
        }
    };

    // Reset to page 1 when filters change
    useEffect(() => {
        setPage(1);
    }, [searchTerm, selectedTags, assignedFilter, enGestionFilter, serviceWindowFilter, managementStateFilter, sortBy, dateRange, showInboundUnanswered]);

    // Reset to page 1 when active role changes
    useEffect(() => {
        setPage(1);
        setConversations([]);
        setRefreshTrigger(prev => prev + 1);
    }, [activeRole?.id]);

    // Fetch inbox
    useEffect(() => {
        const controller = new AbortController();
        let active = true;
        const revision = inboxRevisionRef.current;
        const fetchInbox = async () => {
            setLoading(true);
            setError(null);
            try {
                const offset = (page - 1) * PAGE_SIZE;
                const roleParam = activeRole?.id ? `&role_id=${activeRole.id}` : '';
                
                // Build filter params
                const assignedToParam = agentInfo?.email ? `&assigned_to=${encodeURIComponent(agentInfo.email)}` : '';
                const assignedFilterParam = `&assigned_filter=${assignedFilter}`;
                const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';
                const tagsParam = selectedTags.length > 0 ? `&tags=${selectedTags.join(',')}` : '';
                const inboundUnansweredParam = showInboundUnanswered ? '&inbound_unanswered=true' : '';
                const enGestionFilterParam = `&en_gestion_filter=${enGestionFilter}&view=${enGestionFilter === 'true' ? 'operational' : 'agency'}`;
                const serviceWindowParam = serviceWindowFilter.length > 0 ? `&service_window_filter=${serviceWindowFilter.join(',')}` : '';
                const managementStateParam = managementStateFilter.length > 0 ? `&management_state_filter=${managementStateFilter.join(',')}` : '';
                const sortByParam = `&sort_by=${sortBy}`;
                const dateFromParam = dateRange.from ? `&last_interaction_from=${encodeURIComponent(dateRange.from)}` : '';
                const dateToParam = dateRange.to ? `&last_interaction_to=${encodeURIComponent(dateRange.to)}` : '';
                
                const res = await authFetch(`/api/whatsapp/inbox?limit=${PAGE_SIZE}&offset=${offset}${roleParam}${assignedToParam}${assignedFilterParam}${searchParam}${tagsParam}${inboundUnansweredParam}${enGestionFilterParam}${serviceWindowParam}${managementStateParam}${sortByParam}${dateFromParam}${dateToParam}`, { signal: controller.signal });
                if (!res.ok) throw new Error('Error al cargar la bandeja de entrada');
                const data = await res.json();
                if (!active || revision !== inboxRevisionRef.current) return;
                
                if (data && data.conversations) {
                    console.log('[WA Inbox Sidebar] ✅ Received conversations:', data.conversations.length);
                    setConversations(prev => {
                        // Merge preserving optimistic contact_name/phone_number if backend returns null
                        let merged = data.conversations.map(incoming => {
                            const existing = prev.find(c => c.conversation_id === incoming.conversation_id);
                            if (!existing) return incoming;
                            return {
                                ...incoming,
                                contact_name: incoming.contact_name || existing.contact_name,
                                phone_number: incoming.phone_number || existing.phone_number,
                            };
                        });

                        // Keep the conversation currently open in the viewer even if this page's
                        // filtered results from the backend no longer include it.
                        const activeId = activeConversationIdRef.current;
                        if (activeId && !merged.some(c => c.conversation_id === activeId)) {
                            const activeConv = prev.find(c => c.conversation_id === activeId);
                            if (activeConv && !activeConv.decision_flow_managed) merged = [activeConv, ...merged];
                        }

                        return merged;
                    });
                    setTotal(data.total || 0);
                } else {
                    console.log('[WA Inbox Sidebar] ✅ Received data (array format):', data?.length || 0);
                    setConversations(data || []);
                    setTotal(data?.length || 0);
                }
            } catch (err) {
                if (!active || err.name === 'AbortError') return;
                console.error('[WA Inbox Sidebar] Error fetching:', err);
                setError(err.message);
            } finally {
                if (active) setLoading(false);
            }
        };

        fetchInbox();
        return () => { active = false; controller.abort(); };
    }, [page, refreshTrigger, authFetch, activeRole?.id, assignedFilter, showInboundUnanswered, enGestionFilter, serviceWindowFilter, managementStateFilter, sortBy, dateRange, agentInfo?.email, searchTerm, selectedTags]);

    // Sync 2.0: Unified Refresh and Discovery Logic
    const buildInboxItemUrl = useCallback((conversationId) => {
        const params = new URLSearchParams();
        if (activeRole?.id) params.set('role_id', activeRole.id);
        if (agentInfo?.email) params.set('assigned_to', agentInfo.email);
        params.set('assigned_filter', assignedFilter);
        if (searchTerm) params.set('search', searchTerm);
        if (selectedTags.length > 0) params.set('tags', selectedTags.join(','));
        if (showInboundUnanswered) params.set('inbound_unanswered', 'true');
        params.set('en_gestion_filter', enGestionFilter);
        params.set('view', enGestionFilter === 'true' ? 'operational' : 'agency');
        if (serviceWindowFilter.length > 0) params.set('service_window_filter', serviceWindowFilter.join(','));
        if (managementStateFilter.length > 0) params.set('management_state_filter', managementStateFilter.join(','));
        params.set('sort_by', sortBy);
        if (dateRange.from) params.set('last_interaction_from', dateRange.from);
        if (dateRange.to) params.set('last_interaction_to', dateRange.to);
        // Never let the conversation currently open in the viewer disappear due to a filter mismatch
        if (activeConversationIdRef.current && String(activeConversationIdRef.current) === String(conversationId)) {
            params.set('pin_conversation_id', conversationId);
        }

        const queryString = params.toString();
        return `/api/whatsapp/inbox/item/${conversationId}${queryString ? `?${queryString}` : ''}`;
    }, [activeRole?.id, agentInfo?.email, assignedFilter, searchTerm, selectedTags, showInboundUnanswered, enGestionFilter, serviceWindowFilter, managementStateFilter, sortBy, dateRange]);

    const refreshConversationNow = useCallback(async (conversationId, retryCount = 0, force = false) => {
        if (!conversationId) return;
        
        // Prevent duplicate concurrent refreshes for the same conversation
        const inProgress = refreshInProgressRef.current.get(conversationId);
        if (inProgress !== undefined) {
            const pending = refreshPendingRef.current.get(conversationId) || { retryCount: 0, force: false };
            refreshPendingRef.current.set(conversationId, {
                retryCount: Math.max(pending.retryCount, retryCount),
                force: pending.force || force,
            });
            return;
        }
        
        // Mark this conversation as being refreshed with current retry count
        refreshInProgressRef.current.set(conversationId, retryCount);
        const revision = inboxRevisionRef.current;
        
        const insertion = recentlyInsertedConversationsRef.current.get(conversationId);
        const isRecentlyInserted = insertion && Date.now() - insertion.insertedAt < 30000;
        if (insertion && !isRecentlyInserted) recentlyInsertedConversationsRef.current.delete(conversationId);

        try {
            if (isRecentlyInserted && retryCount === 0) {
                await new Promise(resolve => setTimeout(resolve, 1200));
            }
            console.log(`[WA Sync 2.0] 🔄 Refreshing ${conversationId} (Attempt ${retryCount + 1}${isRecentlyInserted ? ', NEW' : ''})`);
            
            // Build URL with all active filters so the backend can exclude conversations
            // that no longer match the current inbox view.
            const url = buildInboxItemUrl(conversationId);
            
            const res = await authFetch(url);
            if (revision !== inboxRevisionRef.current) return;
            
            if (!res.ok) {
                if (res.status === 401) {
                    console.warn(`[WA Sync 2.0] Ignoring refresh after unauthorized response for ${conversationId}`);
                    return;
                }
                if (res.status === 404) {
                    const errorBody = await res.json().catch(() => ({}));
                    if (revision !== inboxRevisionRef.current) return;
                    const reason = errorBody.reason || 'not_found';
                    if (inboxMissesRef.current.size >= 500) {
                        const oldestMiss = inboxMissesRef.current.values().next().value;
                        inboxMissesRef.current.delete(oldestMiss);
                    }
                    inboxMissesRef.current.add(conversationId);

                    if (reason === 'missing_tramite' && isRecentlyInserted && !insertion.retryUsed && retryCount === 0) {
                        // A just-created conversation can arrive before its trámite link.
                        insertion.retryUsed = true;
                        const nextRetryDelay = 1000;
                        const pendingRefresh = refreshPendingRef.current.get(conversationId);
                        if (pendingRefresh) {
                            // Reuse the coalesced trailing refresh instead of scheduling another request.
                            refreshPendingRef.current.set(conversationId, { retryCount: 1, force: true });
                        } else {
                            console.log(`[WA Sync 2.0] ⏳ ${conversationId} has no trámite yet; retrying once in ${nextRetryDelay / 1000}s`);
                            setTimeout(() => refreshConversationRef.current(conversationId, 1, true), nextRetryDelay);
                        }
                        return;
                    }

                    recentlyInsertedConversationsRef.current.delete(conversationId);

                    // Keep the open conversation visible, but do not retry a filtered 404.
                    if (reason === 'bot_managed' && String(activeConversationIdRef.current) === String(conversationId)) {
                        onOpenConversation(null);
                        setCurrentTramite(null);
                    }
                    if (reason !== 'bot_managed' && String(activeConversationIdRef.current) === String(conversationId)) {
                        console.log(`[WA Sync 2.0] 📌 Keeping active conversation ${conversationId}; inbox result reason=${reason}`);
                        return;
                    }

                    console.log(`[WA Sync 2.0] 🚫 Removing ${conversationId} from inbox after 404 (reason=${reason})`);
                    setConversations(prev => {
                        const wasPresent = prev.some(c => c.conversation_id === conversationId);
                        if (!wasPresent) return prev;
                        setTotal(t => Math.max(0, t - 1));
                        return prev.filter(c => c.conversation_id !== conversationId);
                    });
                    return;
                }
                if (res.status !== 404) {
                    throw new Error(`HTTP ${res.status}`);
                }
                return;
            }

            recentlyInsertedConversationsRef.current.delete(conversationId);
            inboxMissesRef.current.delete(conversationId);
            const updatedItem = await res.json();
            if (revision !== inboxRevisionRef.current) return;
            
            setConversations(prev => {
                const index = prev.findIndex(c => c.conversation_id === conversationId);
                
                if (index !== -1) {
                    // Update existing
                    const existing = prev[index];
                    const existingTagsStr = JSON.stringify(existing.tags || []);
                    const newTagsStr = JSON.stringify(updatedItem.tags || []);

                    const hasRelevantChanges = 
                        existing.last_message !== updatedItem.last_message ||
                        existing.attention_required !== updatedItem.attention_required ||
                        existing.status !== updatedItem.status ||
                        existing.mode !== updatedItem.mode ||
                        existing.mode_revision !== updatedItem.mode_revision ||
                        existing.can_operate !== updatedItem.can_operate ||
                        existing.bot_return_pending !== updatedItem.bot_return_pending ||
                        existing.en_gestion !== updatedItem.en_gestion ||
                        existing.management_state !== updatedItem.management_state ||
                        existing.defer_until !== updatedItem.defer_until ||
                        existing.service_window_until !== updatedItem.service_window_until ||
                        existing.contact_name !== updatedItem.contact_name ||
                        existing.phone_number !== updatedItem.phone_number ||
                        existing.tramite_id !== updatedItem.tramite_id ||
                        existing.assigned_agent_id !== updatedItem.assigned_agent_id ||
                        existingTagsStr !== newTagsStr;

                    // Notificar por CADA nuevo mensaje recibido (cambia last_message y requiere atención)
                    // o si pasa de no requerir atención a requerirla.
                    if (!isManagedReadOnly(updatedItem) && updatedItem.attention_required &&
                        (existing.last_message !== updatedItem.last_message || !existing.attention_required)) {
                        triggerNotification('Nuevo mensaje de WhatsApp', `De: ${updatedItem.contact_name || updatedItem.phone_number}`);
                    }

                    if (!hasRelevantChanges && retryCount === 0) {
                        console.log(`[WA Sync 2.0] ⏭️ No changes for ${conversationId}.`);
                        return prev;
                    }

                    console.log(`[WA Sync 2.0] ✨ Updating ${conversationId}`);
                    const newArr = [...prev];
                    // Preserve contact_name and phone_number from optimistic entry if backend returns null
                    const merged = {
                        ...existing,
                        ...updatedItem,
                        contact_name: updatedItem.contact_name || existing.contact_name,
                        phone_number: updatedItem.phone_number || existing.phone_number,
                    };
                    newArr[index] = merged;
                    
                    // Always move to top on update if on page 1
                    if (page === 1) {
                        newArr.splice(index, 1);
                        newArr.unshift(merged);
                    }
                    return newArr;
                } else {
                    // New conversation discovered
                    if (!isManagedReadOnly(updatedItem) && updatedItem.attention_required) {
                        triggerNotification('Nueva conversación', `De: ${updatedItem.contact_name || updatedItem.phone_number}`);
                    }

                    if (page === 1) {
                        console.log(`[WA Sync 2.0] 🆕 PREPENDING new conversation ${conversationId}`);
                        setTotal(t => t + 1);
                        return [updatedItem, ...prev];
                    }
                    return prev;
                }
            });
            
        } catch (err) {
            console.error(`[WA Sync 2.0] ❌ Error refreshing ${conversationId}:`, err);
        } finally {
            refreshInProgressRef.current.delete(conversationId);
            const pending = refreshPendingRef.current.get(conversationId);
            if (pending !== undefined) {
                refreshPendingRef.current.delete(conversationId);
                refreshConversationRef.current(conversationId, pending.retryCount, pending.force);
            }
        }
    }, [authFetch, page, buildInboxItemUrl, onOpenConversation, setCurrentTramite, triggerNotification]);

    const refreshConversation = useCallback((conversationId, retryCount = 0, force = false) => {
        if (!conversationId) return;

        if (inboxMissesRef.current.has(conversationId) && !force) return;
        if (force) inboxMissesRef.current.delete(conversationId);

        if (refreshInProgressRef.current.has(conversationId)) {
            const pending = refreshPendingRef.current.get(conversationId) || { retryCount: 0, force: false };
            refreshPendingRef.current.set(conversationId, {
                retryCount: Math.max(pending.retryCount, retryCount),
                force: pending.force || force,
            });
            return;
        }

        const existing = refreshDebounceTimersRef.current.get(conversationId);
        if (existing) {
            clearTimeout(existing.timer);
            retryCount = Math.max(retryCount, existing.retryCount);
            force = force || existing.force;
        }

        const timer = setTimeout(() => {
            refreshDebounceTimersRef.current.delete(conversationId);
            refreshConversationNow(conversationId, retryCount, force);
        }, 200);
        refreshDebounceTimersRef.current.set(conversationId, { timer, retryCount, force });
    }, [refreshConversationNow]);

    // La suscripción realtime de abajo no se recrea cuando cambian los filtros
    // (deps [agentInfo?.id, page]); este ref evita que use una closure vieja de refreshConversation.
    const refreshConversationRef = useRef(refreshConversation);
    useEffect(() => {
        refreshConversationRef.current = refreshConversation;
    }, [refreshConversation]);

    useEffect(() => () => {
        sessionRequestRef.current += 1;
        refreshDebounceTimersRef.current.forEach(({ timer }) => clearTimeout(timer));
        refreshDebounceTimersRef.current.clear();
    }, []);

    useEffect(() => {
        const agentId = agentInfo?.id;
        const channels = [];

        // Single channel for all WA sync events
        const channelName = agentId ? `wa-sync-v2-${agentId}` : 'wa-global-sync';
        const syncChannel = configureChannel(channelName, agentId ? 'AgentLink' : 'Global');
        channels.push(syncChannel);

        function configureChannel(name, debugLabel) {
            console.log(`[WA Sync 2.0] 📡 Subscribing to ${debugLabel} channel...`);

            return subscribeInboxChanges(supabase, name, {
                conversations: (payload) => {
                    const id = payload.new?.id || payload.old?.id;
                    console.log(`[WA Sync 2.0][${debugLabel}] 🟢 Conversation ${payload.eventType}: ${id}`);
                    if (id && payload.eventType === 'INSERT') {
                        recentlyInsertedConversationsRef.current.set(id, { insertedAt: Date.now(), retryUsed: false });
                    }

                    // ⚡ INSTANT PREEMPTIVE UI UPDATE (Only if it's an assignment change)
                    if (payload.new && 'assigned_agent_id' in payload.new) {
                        const newAgent = payload.new.assigned_agent_id;

                        // Capturar la asignación previa (antes de este evento) para poder distinguir
                        // un "robo" real (yo tenía el control y lo perdí) de simplemente recibir una
                        // actualización de una conversación que ya estaba bloqueada por otro agente
                        // mientras la estoy viendo en modo solo lectura.
                        const previousConv = conversationsRef.current.find(c => c.conversation_id === id);
                        const previousAgent = previousConv?.assigned_agent_id;

                        setConversations(prev => {
                            const index = prev.findIndex(c => c.conversation_id === id);
                            if (index === -1) return prev;
                            
                            // Check if value actually changed before triggering re-render
                            if (prev[index].assigned_agent_id === newAgent) {
                                return prev;
                            }

                            const newArr = [...prev];
                            newArr[index] = { ...newArr[index], assigned_agent_id: newAgent };
                            return newArr;
                        });
                        
                        // GLOBAL STEAL DETECTION
                        console.log(`[WA Sync 2.0] Check Steal: ActiveRef=${activeConversationIdRef.current}, Payload=${id}, NewAgent=${newAgent}, Me=${agentInfo?.email}`);
                        
                        // Robust comparison (using activeConversationId from REF to avoid stale closure)
                        const isActiveConv = activeConversationIdRef.current && String(activeConversationIdRef.current) === String(id);
                        const isAssignedToOther = newAgent && agentInfo?.email && 
                                                String(newAgent).toLowerCase() !== String(agentInfo.email).toLowerCase();
                        // Solo es un "robo" real si antes la conversación era mía o no estaba asignada.
                        // Si ya estaba asignada a otro agente antes de este evento (p.ej. la estoy viendo
                        // en modo solo lectura), no debe dispararse el aviso de robo.
                        const wasMineOrUnassigned = !previousAgent || 
                                                (agentInfo?.email && String(previousAgent).toLowerCase() === String(agentInfo.email).toLowerCase());

                        if (isActiveConv && isAssignedToOther && wasMineOrUnassigned) {
                            console.log(`[WA Sync 2.0] 🚨 STOLEN DETECTED via Realtime! Ref-based match found.`);
                            
                            // NUEVO: Cerrar panel de adjuntos si estaba abierto
                            setIsAttachmentsOpen(false);
                            
                            if (onStolen) {
                                onStolen(newAgent);
                            }
                        }
                    }

                    refreshConversationRef.current(id, 0, true);
                },
                messages: (payload) => {
                    const id = payload.new?.conversation_id;
                    const direction = payload.new?.direction;
                    console.log(`[WA Sync 2.0][${debugLabel}] 💬 Message INSERT in: ${id} (${direction})`);
                    refreshConversationRef.current(id, 0, refreshRelevantFiltersRef.current.messagesCanChangeFilters);

                    // Detección de CLID Google Message Ads + auto-creación de lead Zoho.
                    // El backend es idempotente: cualquier agente con el inbox abierto puede dispararlo.
                    if (id && direction === 'INBOUND') {
                        authFetch('/api/whatsapp/process-inbound-first-message', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ conversation_id: id }),
                        })
                            .then(r => r.json().then(j => ({ status: r.status, body: j })))
                            .then(({ status, body }) => {
                                if (status >= 200 && status < 300) {
                                    console.log(`[WA Sync 2.0] 🔍 process-inbound result for ${id}:`, body);
                                } else {
                                    console.warn(`[WA Sync 2.0] ⚠️ process-inbound HTTP ${status} for ${id}:`, body);
                                }
                            })
                            .catch(err => console.warn(`[WA Sync 2.0] ⚠️ process-inbound error for ${id}:`, err));
                    }
                },
                integration_events: (payload) => {
                    const id = payload.new?.conversation_id;
                    const type = payload.new?.event_type;
                    console.log(`[WA Sync 2.0][${debugLabel}] 🧪 Integration Event (${type}): ${id}`);
                    if (id) refreshConversationRef.current(id, 0, true);
                },
                whatsapp_conversations_tramites: (payload) => {
                    if (payload.new?.deferred_by_agent_id === agentInfo?.email ||
                        payload.old?.deferred_by_agent_id === agentInfo?.email) {
                        refreshRemindersRef.current();
                    }
                    const id = payload.new?.conversation_id || payload.old?.conversation_id;
                    console.log(`[WA Sync 2.0][${debugLabel}] 🔗 Link ${payload.eventType}: ${id}`);
                    refreshConversationRef.current(id, 0, true);
                },
                object_tags: (payload) => {
                    const id = payload.new?.object_id || payload.old?.object_id;
                    if (id) {
                        console.log(`[WA Sync 2.0][${debugLabel}] 🏷️ Tag ${payload.eventType}: ${id}`);
                        refreshConversationRef.current(id, 0, refreshRelevantFiltersRef.current.tagsCanChangeFilters);
                    }
                },
            }, (status) => {
                console.log(`[WA Sync 2.0][${debugLabel}] Status: ${status}`);
                if (status === 'SUBSCRIBED' || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
                    inboxMissesRef.current.clear();
                    setRefreshTrigger(prev => prev + 1);
                }
            });
        }

        return () => {
            console.log(`[WA Sync 2.0] ⏏️ Unsubscribing all channels`);
            channels.forEach(ch => supabase.removeChannel(ch));
        };
        // onStolen, refreshConversation, and agentInfo.email are intentionally omitted to avoid re-subscription
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agentInfo?.id, page]);
    const handleLinkAndOpen = async (conv, force = false) => {
        const sessionRequest = ++sessionRequestRef.current;
        if (isManagedReadOnly(conv)) {
            onOpenHistoricalConversation?.(conv.conversation_id);
            return;
        }
        console.log('[WA Inbox Sidebar] 🚀 Opening session for:', conv.conversation_id, 'Force:', force);

        if (conv.phone_number) {
            fetchLeadByPhone(conv.phone_number).catch(err =>
                console.error('[WA Inbox Sidebar] Background lead fetch error:', err)
            );
        }

        // NUEVO: Revisar si la conversación ya está asignada a otro antes de intentar abrir
        const isLockedBySomeoneElse = conv.assigned_agent_id && 
                                    String(conv.assigned_agent_id).toLowerCase() !== String(agentInfo?.email).toLowerCase();

        // Si está bloqueada por otro agente y no estamos tomando control (force),
        // abrimos el chat en modo SOLO LECTURA: el agente puede ver todos los mensajes
        // pero no puede escribir ni enviar plantillas. No se llama a open-session
        // para no intentar reclamar la conversación solo por mirarla.
        // Status se marca directamente como 'OPEN' (no 'LOADING') porque no hay ninguna
        // llamada asíncrona pendiente que lo cambie después; de lo contrario el overlay
        // "Abriendo Sesión" de SalesForm se queda visible indefinidamente.
        if (isLockedBySomeoneElse && !force) {
            onOpenConversation(conv.conversation_id);
            setCurrentTramite({
                id: conv.tramite_id || 'loading',
                phone: conv.phone_number,
                contact_id: conv.contact_id,
                status: 'OPEN',
                conversationMetadata: {
                    id: conv.conversation_id,
                    ...conv
                }
            });
            return;
        }

        if (!agentInfo?.id) {
            console.error('[WA Inbox Sidebar] No agent info available');
            onOpenConversation(conv.conversation_id);
            return;
        }

        // STEP 1: Instant Context Switch (Perceived Performance)
        // Switch to the conversation ID immediately
        onOpenConversation(conv.conversation_id);
        
        // Optimistic UI: Set base info in current tramite
        setCurrentTramite({
            id: conv.tramite_id || 'loading',
            phone: conv.phone_number,
            contact_id: conv.contact_id,
            status: 'LOADING',
            conversationMetadata: {
                id: conv.conversation_id,
                ...conv,
                assigned_agent_id: force && agentInfo?.email ? agentInfo.email : conv.assigned_agent_id
            }
        });

        try {
            // STEP 2: Unified Backend Call (Orchestration)
            const response = await authFetch('/api/whatsapp/open-session', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    conversation_id: conv.conversation_id,
                    agent_email: agentInfo.email,
                    agent_id: agentInfo.id,
                    phone: conv.phone_number,
                    contact_id: conv.contact_id,
                    force
                })
            });
            if (sessionRequest !== sessionRequestRef.current) return;

            if (response.status === 409) {
                const body = await response.json().catch(() => ({}));
                alert(body.error || 'Esta conversación ya está siendo atendida por otro agente.');
                onOpenConversation(null); // Close if forbidden
                setCurrentTramite(null);
                refreshConversationRef.current(conv.conversation_id, 0, true);
                return;
            }

            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                throw new Error(body.error || 'Error al abrir la conversación');
            }

            const result = await response.json();
            if (sessionRequest !== sessionRequestRef.current) return;
            const { tramite_id, form_data, conversation } = result;
            inboxRevisionRef.current += 1;
            setConversations(prev => mergeOpenedSession(prev, conv, result));
            refreshConversationRef.current(conv.conversation_id, 0, true);
            setRefreshTrigger(prev => prev + 1);
            
            console.log(`[WA Inbox Sidebar] ✅ Session opened. Trámite: ${tramite_id}`);

            // STEP 3: Final Context Update & Data Injection
            // We pass preHydratedData and the enriched conversation metadata 
            // so SalesDataContext and Appendix can skip redundant fetches
            setCurrentTramite({
                id: tramite_id,
                phone: conv.phone_number,
                contact_id: conv.contact_id,
                status: 'OPEN',
                preHydratedData: form_data,
                conversationMetadata: conversation
            });

            if (form_data) {
                updateSectionData('SalesForm', form_data, 'zoho');
            }

        } catch (err) {
            if (sessionRequest !== sessionRequestRef.current) return;
            console.error('[WA Inbox Sidebar] Error in session opening:', err);
            onOpenConversation(null);
            setCurrentTramite(null);
            alert(err.message || 'Error al abrir la conversación');
        }
    };

    const closeExpiredRef = useRef(null);
    closeExpiredRef.current = () => {
        const active = conversationsRef.current.find(c => String(c.conversation_id) === String(activeConversationIdRef.current));
        if (isManagedBotHidden(active)) {
            onOpenConversation(null);
            setCurrentTramite(null);
        }
        inboxMissesRef.current.clear();
        if (activeConversationIdRef.current) {
            refreshConversationRef.current(activeConversationIdRef.current, 0, true);
        }
        setRefreshTrigger(prev => prev + 1);
    };
    useEffect(() => startInboxRecovery(() => closeExpiredRef.current()), []);

    useEffect(() => {
        const deadlines = conversations.filter(c => c.decision_flow_managed && c.mode === 'human_active')
            .map(c => new Date(c.service_window_until).getTime()).filter(t => t > Date.now());
        const timer = deadlines.length ? setTimeout(() => closeExpiredRef.current(), Math.min(...deadlines) - Date.now() + 1) : null;
        return () => { if (timer) clearTimeout(timer); };
    }, [conversations]);

    const attemptLockAndOpen = (conv, force = false, event = null) => {
        if (event) event.stopPropagation();
        // Siempre cerrar adjuntos al cambiar/cargar conversación
        setIsAttachmentsOpen(false);
        return handleLinkAndOpen(conv, force);
    };

    const fetchTemplatesForRole = async () => {
        if (!activeRole) {
            console.log('[WA Inbox Sidebar] No active role, clearing templates');
            setAvailableTemplates([]);
            return;
        }

        setTemplatesLoading(true);
        try {
            const res = await authFetch(`/api/whatsapp/templates/my-role?role_id=${activeRole.id}`);
            if (res.ok) {
                const data = await res.json();
                console.log('[WA Inbox Sidebar] Fetched templates for role:', data.templates);
                setAvailableTemplates(data.templates || []);
            } else {
                console.error('[WA Inbox Sidebar] Failed to fetch templates');
                setAvailableTemplates([]);
            }
        } catch (err) {
            console.error('[WA Inbox Sidebar] Error fetching templates:', err);
            setAvailableTemplates([]);
        } finally {
            setTemplatesLoading(false);
        }
    };

    const handleStartNewConversation = async (templateId) => {
        if (!newConvPhone) {
            alert('Por favor ingresa un número de teléfono');
            return;
        }
        
        // Validate channel selection if role has channels assigned
        if (activeRole?.channels?.length > 0 && !newConvChannelId) {
            alert('Por favor selecciona un canal');
            return;
        }
        
        try {
            console.log('[WA Inbox Sidebar] Starting new conversation with phone:', newConvPhone, 'template:', templateId, 'channel:', newConvChannelId || 'default');
            const payload = {
                phone: newConvPhone,
                template_id: templateId,  // Send UUID
                agent_id: agentInfo?.id
            };
            
            // Include channel_id if selected (for roles with multiple channels)
            if (newConvChannelId) {
                payload.channel_id = newConvChannelId;
            }
            
            const res = await authFetch('/api/whatsapp/start-conversation-with-tramite', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                const data = await res.json();
                console.log('[WA Inbox Sidebar] ✅ Successfully started conversation and tramite:', data);
                
                // Update context to load the new tramite
                setCurrentTramite({
                    id: data.tramite_id,
                    phone: newConvPhone,
                    status: 'OPEN'
                });

                // If Hub didn't return conversation_id synchronously, retry linking via wa_message_id
                if (!data.conversation_id && data.wa_message_id && data.tramite_id) {
                    const retryLink = async (attempt = 0) => {
                        if (attempt > 8) return;
                        const delay = Math.min(1000 * Math.pow(1.5, attempt), 10000);
                        await new Promise(r => setTimeout(r, delay));
                        try {
                            const linkRes = await authFetch('/api/whatsapp/link-tramite-by-wa-message', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ 
                                    wa_message_id: data.wa_message_id, 
                                    tramite_id: data.tramite_id,
                                    agent_email: agentInfo?.email
                                })
                            });
                            const linkData = await linkRes.json();
                            if (linkData.linked) {
                                console.log('[WA Inbox Sidebar] ✅ Deferred link resolved:', linkData.conversation_id);
                                setRefreshTrigger(prev => prev + 1);
                            } else {
                                retryLink(attempt + 1);
                            }
                        } catch (e) {
                            retryLink(attempt + 1);
                        }
                    };
                    retryLink();
                }

                // Optimistically inject the new conversation into the inbox list
                // so the phone number is visible immediately, before the backend
                // resolves the Hub conversation_id asynchronously
                if (data.conversation_id && !isManagedBotHidden(data.conversation_state)) {
                    const optimisticEntry = {
                        ...data.conversation_state,
                        conversation_id: data.conversation_id,
                        tramite_id: data.tramite_id,
                        contact_name: newConvPhone,
                        phone_number: newConvPhone,
                        last_message: null,
                        last_message_at: new Date().toISOString(),
                        attention_required: false,
                        status: 'open',
                        tags: [],
                        address: null,
                    };
                    setConversations(prev => {
                        const exists = prev.some(c => c.conversation_id === data.conversation_id);
                        return exists ? prev : [optimisticEntry, ...prev];
                    });
                }

                // Open the conversation view
                if (!isManagedBotHidden(data.conversation_state)) {
                    onOpenConversation({
                        conversation_id: data.conversation_id,
                        wa_message_id: data.wa_message_id
                    });
                }

                // Close modal and reset
                setIsNewConvModalOpen(false);
                setNewConvPhone('');
                setNewConvChannelId('');
                setRefreshTrigger(prev => prev + 1);
            } else {
                // Parse error response
                let errorMsg = 'Error desconocido';
                try {
                    const errorData = await res.json();
                    if (errorData.details) {
                        errorMsg = errorData.details;
                    } else if (errorData.error) {
                        errorMsg = errorData.error;
                    }
                } catch {
                    errorMsg = await res.text();
                }
                console.error('[WA Inbox Sidebar] Error starting conversation:', errorMsg);
                alert(`Error al iniciar conversación: ${errorMsg}`);
            }
        } catch (err) {
            console.error('[WA Inbox Sidebar] Error starting new conversation:', err);
            alert(`Error al iniciar conversación: ${err.message || err}`);
        }
    };

    const handleTemplateError = (err, templateName = 'esta plantilla') => {
        if (err.status === 409) {
            alert(err.message || `Ya hay un proceso abierto para este contacto al enviar ${templateName}.`);
        } else {
            console.error('[WA Inbox Sidebar] Template error:', err);
            alert(err.message || 'No se pudo enviar la plantilla');
        }
    };

    const handleToggleEnGestion = async (conversationId, currentValue) => {
        inboxRevisionRef.current += 1;
        try {
            const newValue = !currentValue;
            
            // Optimistic update: actualizar UI inmediatamente
            setConversations(prev => prev.map(conv => 
                conv.conversation_id === conversationId 
                    ? { ...conv, en_gestion: newValue }
                    : conv
            ));
            
            // Llamar al backend
            const response = await authFetch(
                `/api/whatsapp/conversation/${conversationId}/en-gestion`,
                {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ en_gestion: newValue })
                }
            );
            
            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                refreshConversationRef.current(conversationId, 0, true);
                throw new Error(body.error || 'Error al actualizar estado de gestión');
            }

            const result = await response.json();
            inboxRevisionRef.current += 1;
            setConversations(prev => prev.map(conv => conv.conversation_id === conversationId
                ? { ...conv, en_gestion: result.en_gestion ?? newValue,
                    bot_return_pending: result.bot_return_pending || false,
                    can_operate: result.bot_return_pending ? false : conv.can_operate }
                : conv));
            if (result.bot_return_pending) {
                window.dispatchEvent(new CustomEvent('wa-lifecycle-updated', {
                    detail: { conversation_id: conversationId, bot_return_pending: true, can_operate: false }
                }));
                setRefreshTrigger(prev => prev + 1);
                return;
            }
            if (result.status === 'superseded') {
                setRefreshTrigger(prev => prev + 1);
                return;
            }
            const existing = conversationsRef.current.find(conv => conv.conversation_id === conversationId);
            if (existing?.decision_flow_managed && !result.en_gestion) {
                setConversations(prev => prev.filter(conv => conv.conversation_id !== conversationId));
                if (String(activeConversationIdRef.current) === String(conversationId)) {
                    onOpenConversation(null);
                    setCurrentTramite(null);
                }
                setRefreshTrigger(prev => prev + 1);
                return;
            }
            if (result.bot_mode_updated === false) {
                alert(result.warning || 'La conversación se archivó, pero no se pudo activar el bot en WhatsApp Hub.');
            }
            refreshConversationRef.current(conversationId, 0, true);
            setRefreshTrigger(prev => prev + 1);
            
            // Si el filtro actual NO muestra conversaciones fuera de gestión,
            // y acabamos de poner en_gestion=false, quitar de la lista.
            // Excepción: si es la conversación abierta en el visor, se mantiene visible.
            if (enGestionFilter === 'true' && !newValue && conversationId !== activeConversationId) {
                setConversations(prev => 
                    prev.filter(conv => conv.conversation_id !== conversationId)
                );
                setTotal(prev => prev - 1);
            }
            
            console.log(`[WA Inbox] Conversación ${conversationId} → en_gestion=${newValue}`);
            
        } catch (err) {
            console.error('[WA Inbox] Error toggling en_gestion:', err);
            // Revertir optimistic update
            setConversations(prev => prev.map(conv => 
                conv.conversation_id === conversationId 
                    ? { ...conv, en_gestion: currentValue }
                    : conv
            ));
            alert(err.message || 'Error al cambiar estado de gestión');
        }
    };

    // Using reusable WhatsAppTemplateButton for inbox actions

    // Menú de opciones de diferimiento abierto para una conversación concreta (id) o null
    const [deferMenuOpenFor, setDeferMenuOpenFor] = useState(null);

    const DEFER_OPTIONS = [
        { minutes: 15, label: '15 min' },
        { minutes: 30, label: '30 min' },
        { minutes: 60, label: '1 h' },
        { minutes: 120, label: '2 h' },
        { minutes: 240, label: '4 h' },
    ];

    const handleDeferConversation = async (conversationId, minutes) => {
        inboxRevisionRef.current += 1;
        const previous = conversationsRef.current.find(c => c.conversation_id === conversationId);
        const previousState = previous ? {
            defer_until: previous.defer_until,
            deferred_minutes: previous.deferred_minutes,
            management_state: previous.management_state
        } : null;

        try {
            setDeferMenuOpenFor(null);

            // Optimistic update
            const optimisticDeferUntil = minutes ? new Date(Date.now() + minutes * 60000).toISOString() : null;
            setConversations(prev => prev.map(conv =>
                conv.conversation_id === conversationId
                    ? {
                        ...conv,
                        defer_until: optimisticDeferUntil,
                        deferred_minutes: minutes,
                        management_state: minutes ? 'diferida' : null
                    }
                    : conv
            ));

            const response = await authFetch(
                `/api/whatsapp/conversation/${conversationId}/defer`,
                {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ minutes, agent_email: agentInfo?.email })
                }
            );

            if (!response.ok) {
                let errorMsg = 'Error al actualizar el diferimiento';
                try {
                    const errorData = await response.json();
                    errorMsg = errorData.error || errorMsg;
                } catch (e) { /* ignore */ }
                throw new Error(errorMsg);
            }

            const data = await response.json();
            inboxRevisionRef.current += 1;
            setConversations(prev => prev.map(conv =>
                conv.conversation_id === conversationId
                    ? {
                        ...conv,
                        defer_until: data.defer_until,
                        deferred_minutes: data.deferred_minutes,
                        management_state: data.management_state
                    }
                    : conv
            ));

            console.log(`[WA Inbox] Conversación ${conversationId} → defer_until=${data.defer_until}`);
            refreshRemindersRef.current();
            refreshConversationRef.current(conversationId, 0, true);
            setRefreshTrigger(prev => prev + 1);
        } catch (err) {
            console.error('[WA Inbox] Error deferring conversation:', err);
            // Revertir optimistic update
            if (previousState) {
                setConversations(prev => prev.map(conv =>
                    conv.conversation_id === conversationId
                        ? { ...conv, ...previousState }
                        : conv
                ));
            }
            alert(err.message || 'Error al cambiar el diferimiento');
        }
    };

    // Also enforce deadlines between backend refreshes.
    const filteredConversations = enGestionFilter !== 'true'
        ? conversations.filter(conv => !conv.decision_flow_managed || conv.mode === 'human_active' || (!conv.en_gestion && !conv.tree_active))
        : conversations.filter(conv => !isManagedBotHidden(conv));

    const toggleTagFilter = (tagId) => {
        setSelectedTags(prev => 
            prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
        );
    };

    return (
        <div className="flex flex-col flex-1 bg-slate-900/50 border-t border-slate-800 min-h-[300px] overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b border-slate-800 flex-shrink-0">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-emerald-500/10 rounded-lg">
                            <MessageSquare size={14} className="text-emerald-500" />
                        </div>
                        <h3 className="text-slate-200 font-bold text-[12px] uppercase tracking-wider">
                            Bandeja WhatsApp
                            {!loading && total > 0 && (
                                <span className="ml-1.5 text-[11px] font-normal text-slate-400">({total})</span>
                            )}
                        </h3>
                    </div>
                    <div className="flex items-center gap-1">
                        {onToggleExpand && (
                            <button
                                onClick={onToggleExpand}
                                className="p-1 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-slate-200 transition-colors"
                                title={isExpanded ? 'Plegar bandeja' : 'Expandir bandeja'}
                            >
                                {isExpanded ? <ChevronsLeft size={16} /> : <ChevronsRight size={16} />}
                            </button>
                        )}
                        <button
                            onClick={() => {
                                setIsNewConvModalOpen(true);
                                fetchTemplatesForRole();
                            }}
                            className="p-1 hover:bg-slate-800 rounded-lg text-emerald-500 transition-colors"
                            title="Nueva Conversación"
                        >
                            <Plus size={16} />
                        </button>
                    </div>
                </div>
                
                {/* Search Bar */}
                <div className="flex items-center gap-2 relative">
                    <div className="relative flex-1">
                        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                        <input
                            type="text"
                            placeholder="Buscar..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-slate-950 border border-slate-800 rounded-lg pl-8 pr-3 py-1.5 text-[12px] text-slate-200 outline-none focus:ring-1 focus:ring-emerald-500/50 transition-all"
                        />
                    </div>
                    <button
                        onClick={() => setShowInboundUnanswered(!showInboundUnanswered)}
                        className={`flex-shrink-0 p-1.5 rounded-lg transition-all ${
                            showInboundUnanswered 
                                ? 'bg-orange-500/20 text-orange-400 ring-1 ring-orange-500/50' 
                                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                        }`}
                        title="Mostrar solo conversaciones con último mensaje del cliente"
                    >
                        <Bell size={14} />
                    </button>
                    <button
                        onClick={toggleDeferredOnlyView}
                        className={`flex-shrink-0 p-1.5 rounded-lg transition-all ${
                            isDeferredOnlyView
                                ? 'bg-indigo-500/20 text-indigo-400 ring-1 ring-indigo-500/50'
                                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                        }`}
                        title="Ver solo conversaciones diferidas"
                    >
                        <Clock size={14} />
                    </button>
                    <button
                        onClick={() => setIsFilterPanelOpen(prev => !prev)}
                        className={`flex-shrink-0 relative p-1.5 rounded-lg transition-all ${
                            isFilterPanelOpen || activeFilterCount > 0
                                ? 'bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/50'
                                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                        }`}
                        title="Filtros avanzados"
                    >
                        <SlidersHorizontal size={14} />
                        {activeFilterCount > 0 && (
                            <span className="absolute -top-1 -right-1 bg-emerald-500 text-slate-950 text-[9px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center">
                                {activeFilterCount}
                            </span>
                        )}
                    </button>

                    {/* Filter Panel */}
                    {isFilterPanelOpen && (
                        <div className="absolute top-full right-0 mt-2 w-72 bg-slate-950 border border-slate-800 rounded-xl shadow-2xl z-30 p-3 space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-300">Filtros</span>
                                <div className="flex items-center gap-2">
                                    <button onClick={clearAllFilters} className="text-[10px] text-emerald-400 hover:text-emerald-300 font-medium">
                                        Limpiar
                                    </button>
                                    <button onClick={() => setIsFilterPanelOpen(false)} className="text-slate-500 hover:text-slate-300">
                                        <X size={14} />
                                    </button>
                                </div>
                            </div>

                            {/* Asignación */}
                            <div>
                                <label className="text-[10px] text-slate-500 font-semibold uppercase tracking-wide">Asignación</label>
                                <div className="mt-1 grid grid-cols-2 gap-1">
                                    {[
                                        { value: 'all', label: 'Todas' },
                                        { value: 'mine', label: 'Mías' },
                                        { value: 'unassigned', label: 'Sin asignar' },
                                        { value: 'others', label: 'De otro agente' },
                                    ].map(opt => (
                                        <button
                                            key={opt.value}
                                            onClick={() => setAssignedFilter(opt.value)}
                                            className={`text-[10px] px-2 py-1 rounded-md font-medium transition-all ${
                                                assignedFilter === opt.value
                                                    ? 'bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/40'
                                                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                                            }`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Estado de gestión */}
                            <div>
                                <label className="text-[10px] text-slate-500 font-semibold uppercase tracking-wide">Estado de gestión</label>
                                <select
                                    value={enGestionFilter}
                                    onChange={(e) => setEnGestionFilter(e.target.value)}
                                    className="mt-1 w-full bg-slate-800 border border-slate-700 rounded-md px-2 py-1 text-[11px] text-slate-200"
                                >
                                    <option value="true">En gestión</option>
                                    <option value="false">Fuera de gestión</option>
                                    <option value="all">Todas</option>
                                </select>
                            </div>

                            {/* Ventana de servicio */}
                            <div>
                                <label className="text-[10px] text-slate-500 font-semibold uppercase tracking-wide">Ventana de servicio</label>
                                <div className="mt-1 flex flex-wrap gap-1">
                                    {[
                                        { value: 'valid', label: 'Vigente' },
                                        { value: 'expiring_soon', label: 'Expira pronto' },
                                        { value: 'expired', label: 'Caducada' },
                                    ].map(opt => (
                                        <button
                                            key={opt.value}
                                            onClick={() => toggleServiceWindowOption(opt.value)}
                                            className={`text-[10px] px-2 py-1 rounded-md font-medium transition-all ${
                                                serviceWindowFilter.includes(opt.value)
                                                    ? 'bg-amber-500/20 text-amber-400 ring-1 ring-amber-500/40'
                                                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                                            }`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Gestión diferida / inmediata */}
                            <div>
                                <label className="text-[10px] text-slate-500 font-semibold uppercase tracking-wide">Gestión diferida</label>
                                <div className="mt-1 flex flex-wrap gap-1">
                                    {[
                                        { value: 'diferida', label: 'Diferida' },
                                        { value: 'inmediata', label: 'Inmediata' },
                                    ].map(opt => (
                                        <button
                                            key={opt.value}
                                            onClick={() => toggleManagementStateOption(opt.value)}
                                            className={`text-[10px] px-2 py-1 rounded-md font-medium transition-all ${
                                                managementStateFilter.includes(opt.value)
                                                    ? opt.value === 'inmediata'
                                                        ? 'bg-red-500/20 text-red-400 ring-1 ring-red-500/40'
                                                        : 'bg-indigo-500/20 text-indigo-400 ring-1 ring-indigo-500/40'
                                                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                                            }`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Orden */}
                            <div>
                                <label className="text-[10px] text-slate-500 font-semibold uppercase tracking-wide">Ordenar por</label>
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="mt-1 w-full bg-slate-800 border border-slate-700 rounded-md px-2 py-1 text-[11px] text-slate-200"
                                >
                                    <option value="recent">Más reciente</option>
                                    <option value="urgency">Urgencia</option>
                                    <option value="oldest">Más antigua</option>
                                </select>
                            </div>

                            {/* Rango de fechas de última interacción */}
                            <div>
                                <label className="text-[10px] text-slate-500 font-semibold uppercase tracking-wide">Última interacción del cliente</label>
                                <div className="mt-1 grid grid-cols-2 gap-1">
                                    <input
                                        type="date"
                                        value={dateRange.from}
                                        onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                                        className="bg-slate-800 border border-slate-700 rounded-md px-1.5 py-1 text-[10px] text-slate-200"
                                    />
                                    <input
                                        type="date"
                                        value={dateRange.to}
                                        onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                                        className="bg-slate-800 border border-slate-700 rounded-md px-1.5 py-1 text-[10px] text-slate-200"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Tag Filters */}
                {availableTags.length > 0 && (
                    <div className="mt-3 flex items-center flex-wrap gap-1.5 px-1 py-1 rounded-md bg-slate-900/50">
                        {availableTags.map(tag => {
                            const isSelected = selectedTags.includes(tag.id);
                            return (
                                <button
                                    key={tag.id}
                                    onClick={() => toggleTagFilter(tag.id)}
                                    className={`text-[10px] px-2 py-0.5 rounded-sm font-medium transition-all ${
                                        isSelected ? 'ring-1 ring-white/50 opacity-100 shadow-sm' : 'opacity-60 hover:opacity-90'
                                    }`}
                                    style={{ 
                                        backgroundColor: isSelected ? tag.color : `${tag.color}33`, 
                                        color: isSelected ? '#fff' : tag.color 
                                    }}
                                    title={`Filtrar por ${tag.name}`}
                                >
                                    {tag.name}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* List Container */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-1.5">
                {loading && conversations.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-10">
                        <Loader2 size={20} className="text-emerald-500 animate-spin mb-2" />
                        <span className="text-[11px] text-emerald-400 font-bold uppercase tracking-widest">Cargando...</span>
                    </div>
                )}

                {error && (
                    <div className="flex flex-col items-center justify-center p-4 text-center">
                        <AlertCircle size={24} className="text-red-500 mb-2 opacity-50" />
                        <p className="text-[12px] text-slate-400">{error}</p>
                        <button 
                            onClick={() => setRefreshTrigger(prev => prev + 1)}
                            className="mt-3 px-3 py-1.5 bg-slate-900 hover:bg-slate-800 rounded-lg text-[11px] font-bold text-slate-300"
                        >
                            Reintentar
                        </button>
                    </div>
                )}

                {!loading && filteredConversations.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-10 opacity-30 text-center px-4">
                        <MessageSquare size={28} className="text-slate-600 mb-3" />
                        <span className="text-[12px] text-slate-400 font-medium">No hay conversaciones</span>
                    </div>
                )}

                {filteredConversations.map((conv) => {
                    const isLockedByOther = conv.assigned_agent_id &&
                        String(conv.assigned_agent_id).toLowerCase() !== String(agentInfo?.email).toLowerCase();
                    // Nunca se considera "activa/mía" una conversación bloqueada por otro agente,
                    // aunque esté abierta en el visor en modo solo lectura. Esto evita mostrar
                    // acciones de edición (adjuntar, plantillas, en gestión) y mantiene visible
                    // el botón "Tomar Control".
                    const isActive = !isLockedByOther && (
                        (currentTramite?.id && conv.tramite_id && String(currentTramite.id) === String(conv.tramite_id)) || 
                        (currentTramite?.phone && String(currentTramite.phone) === String(conv.phone_number))
                    );
                    
                    // Calculate expiry status based on last client interaction
                    const calculateExpiryStatus = (lastClientInteraction, lastMessageDirection, lastMessageType) => {
                        // Si el último mensaje es un template outbound, siempre está esperando respuesta del cliente
                        const isOutboundTemplate = 
                            (lastMessageDirection === 'OUTBOUND' || lastMessageDirection === 'outbound' || lastMessageDirection === 'out') && 
                            lastMessageType === 'system';
                        
                        if (isOutboundTemplate) return 'awaitingClient'; // Template outbound, esperando cliente
                        
                        if (!lastClientInteraction) return 'expired'; // No client messages
                        
                        const now = Date.now();
                        const lastInteractionTime = new Date(lastClientInteraction).getTime();
                        const hoursSinceLastClient = (now - lastInteractionTime) / (1000 * 60 * 60);
                        
                        if (hoursSinceLastClient > 24) return 'expired';      // >24h = expired
                        if (hoursSinceLastClient > 23) return 'nearExpiry';   // 23-24h = near expiry
                        return 'normal';                                       // <23h = normal
                    };
                    
                    const expiryStatus = calculateExpiryStatus(conv.last_client_interaction, conv.last_message_direction, conv.last_message_type);
                    const isExpired = expiryStatus === 'expired';
                    const isNearExpiry = expiryStatus === 'nearExpiry';
                    const isAwaitingClient = expiryStatus === 'awaitingClient';
                    const isDeferida = conv.management_state === 'diferida';
                    const isInmediata = conv.management_state === 'inmediata';
                    const canManage = isActive && !isManagedReadOnly(conv);
                    // Archiving does not require a client reply or an open messaging window.
                    // A read-only conversation still belongs to its assigned agent.
                    const canArchive = conv.tramite_id && conv.en_gestion && conv.assigned_agent_id &&
                        !isLockedByOther && !conv.bot_return_pending &&
                        (!conv.decision_flow_managed || (conv.mode === 'human_active' && conv.status !== 'closed'));
                    const canDefer = canManage && conv.tramite_id && conv.assigned_agent_id === agentInfo?.email;

                    return (
                    <div
                        key={conv.conversation_id}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                            if (isActive) {
                                // Si ya está activa, verificar si el chat está abierto
                                const whatsappChatAppendix = appendices['whatsapp-chat'];
                                const isCurrentlyOpen = whatsappChatAppendix?.isOpen && activeConversationId === conv.conversation_id;
                                if (!isCurrentlyOpen) {
                                    // Abrir el chat con la conversación actual
                                    onOpenConversation(conv.conversation_id);
                                }
                                return;
                            }
                            // Si está bloqueada por otro agente, se abre igualmente en modo solo lectura
                            attemptLockAndOpen(conv, false, e);
                        }}
                        onKeyDown={(e) => { 
                            if (e.key === 'Enter' || e.key === ' ') {
                                if (isActive) {
                                    // Si ya está activa, verificar si el chat está abierto
                                    const whatsappChatAppendix = appendices['whatsapp-chat'];
                                    const isCurrentlyOpen = whatsappChatAppendix?.isOpen && activeConversationId === conv.conversation_id;
                                    if (!isCurrentlyOpen) {
                                        // Abrir el chat con la conversación actual
                                        onOpenConversation(conv.conversation_id);
                                    }
                                    return;
                                }
                                attemptLockAndOpen(conv, false, e);
                            }
                        }}
                        className={`w-full text-left rounded-xl border transition-all group relative overflow-hidden ${
                            isActive
                                ? 'bg-emerald-500/5 border-emerald-500/30 ring-1 ring-emerald-500/20'
                                : isInmediata
                                    ? 'bg-red-500/10 border-red-500/50 ring-2 ring-red-500/30 shadow-[0_0_14px_rgba(239,68,68,0.35)] animate-[pulse_2s_ease-in-out_infinite]'
                                    : isExpired
                                        ? 'bg-red-500/10 border-red-500/30'
                                        : isNearExpiry
                                            ? 'bg-orange-500/10 border-orange-500/30'
                                            : isAwaitingClient
                                                ? 'border-slate-800/60 bg-slate-900/20 hover:bg-slate-900/60 hover:border-slate-700'
                                                : isLockedByOther
                                                    ? 'border-slate-800/60 bg-slate-900/30'
                                                    : 'border-slate-800/60 bg-slate-900/20 hover:bg-slate-900/60 hover:border-slate-700'
                        }`}
                    >
                        {/* Franja izquierda de estado */}
                        <div className={`absolute left-0 top-0 bottom-0 w-[3px] rounded-l-xl ${
                            isInmediata
                                ? 'bg-red-500 animate-pulse'
                                : !conv.last_message
                                    ? 'bg-sky-500/60'
                                    : conv.attention_required
                                        ? 'bg-emerald-500'
                                        : isActive
                                            ? 'bg-emerald-500/40'
                                            : 'bg-transparent'
                        }`} />

                        <div className={`pl-4 pr-3 min-w-0 overflow-hidden ${isExpanded ? 'py-1.5' : 'py-3'}`}>

                            {/* Fila superior: nombre + (si expandido, teléfono inline) + hora */}
                            <div className={`flex items-center justify-between gap-2 ${isExpanded ? 'mb-0.5' : 'mb-1'}`}>
                                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                    <span className="text-[12px] font-semibold text-slate-100 truncate leading-none">
                                        {conv.contact_name || 'Sin nombre'}
                                    </span>

                                    {isExpanded && conv.phone_number && conv.phone_number !== conv.contact_name && (
                                        <CopyablePhone
                                            phone={conv.phone_number}
                                            className="min-w-0 shrink-0"
                                            textClassName="text-[10px] font-mono text-slate-500"
                                        />
                                    )}

                                    {(conv.assigned_agent_id || conv.last_assigned_agent_id) && (
                                        <span
                                            className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-500/10 border border-amber-500/30 text-[9px] font-bold text-amber-400 leading-none"
                                            title={conv.assigned_agent_id ? `Asignada a ${conv.assigned_agent_id}` : `Último agente: ${conv.last_assigned_agent_id}`}
                                        >
                                            {conv.assigned_agent_id ? <Lock size={8} /> : <History size={8} />}
                                            {(conv.assigned_agent_id || conv.last_assigned_agent_id || '').split('@')[0]}
                                        </span>
                                    )}
                                    
                                    {/* Icono/Enlace a Zoho Lead */}
                                    {conv.zoho_lead_id && (
                                        <a
                                            href={`https://crm.zoho.eu/crm/imaginaenergia/tab/Leads/${conv.zoho_lead_id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="flex-shrink-0 p-0.5 hover:bg-emerald-500/20 rounded transition-colors group/lead"
                                            title={`Ver lead en Zoho CRM (${conv.zoho_lead_id})`}
                                        >
                                            <Contact size={12} className="text-emerald-400 group-hover/lead:text-emerald-300" />
                                        </a>
                                    )}
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    {/* Sin agente asignado */}
                                    {!conv.assigned_agent_id && (
                                        <span
                                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-slate-500/15 border border-slate-500/30 text-[9px] font-bold text-slate-400 leading-none uppercase tracking-wider"
                                            title="Sin agente asignado"
                                        >
                                            Sin asignar
                                        </span>
                                    )}
                                    {/* Sin mensajes aún, esperando respuesta del cliente */}
                                    {!conv.last_message && (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-sky-500/10 border border-sky-500/20 text-[9px] font-bold text-sky-400 leading-none uppercase tracking-wider">
                                            Esperando respuesta
                                        </span>
                                    )}
                                    {/* Expiry status badge */}
                                    {isExpired && (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-red-500/15 border border-red-500/30 text-[9px] font-bold text-red-400 leading-none uppercase tracking-wider">
                                            Caducada
                                        </span>
                                    )}
                                    {!isExpired && isNearExpiry && (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-orange-500/15 border border-orange-500/30 text-[9px] font-bold text-orange-400 leading-none uppercase tracking-wider">
                                            Expira pronto
                                        </span>
                                    )}
                                    {!isExpired && !isNearExpiry && isAwaitingClient && (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-sky-500/15 border border-sky-500/30 text-[9px] font-bold text-sky-400 leading-none uppercase tracking-wider">
                                            Esperando
                                        </span>
                                    )}
                                    {conv.attention_required && (
                                        <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.6)]" />
                                    )}
                                    <span className="text-[10px] font-mono text-slate-200">
                                        {conv.last_message_at ? new Date(conv.last_message_at).toLocaleString([], { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                                    </span>
                                </div>
                            </div>

                            {/* Teléfono (fila separada solo en modo compacto) */}
                            {!isExpanded && conv.phone_number && conv.phone_number !== conv.contact_name && (
                                <div className="flex items-center gap-1 mb-1.5">
                                    <Phone size={9} className="text-slate-500 shrink-0" />
                                    <CopyablePhone
                                        phone={conv.phone_number}
                                        className="min-w-0 flex-1"
                                        textClassName="text-[11px] font-mono text-slate-400"
                                    />
                                </div>
                            )}

                            {/* Último mensaje */}
                            {conv.last_message && (
                                <p className="text-[11px] text-slate-500 truncate leading-snug">
                                    {conv.last_message}
                                </p>
                            )}

                            {/* Tags + badge de gestión (misma fila cuando expandido, para ahorrar alto) */}
                            {((conv.tags && conv.tags.length > 0) || !conv.en_gestion || isDeferida || isInmediata || conv.bot_return_pending) && (
                                <div className={`flex items-center flex-wrap gap-1.5 overflow-hidden ${isExpanded ? 'mt-1' : 'mt-1.5'}`}>
                                    {conv.tags && conv.tags.length > 0 && (
                                        <TagDisplayList tags={conv.tags} />
                                    )}
                                    {conv.bot_return_pending && (
                                        <span className="text-[10px] text-amber-400">Devolución al bot pendiente</span>
                                    )}
                                    {!conv.en_gestion && (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-slate-700/50 border border-slate-600 text-[10px] font-medium text-slate-400 leading-none w-fit">
                                            <Archive size={8} />
                                            Fuera de gestión
                                        </span>
                                    )}
                                    {isInmediata && (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-red-500/20 border border-red-500/40 text-[10px] font-bold text-red-400 leading-none w-fit animate-pulse">
                                            <AlarmClock size={9} />
                                            Gestión inmediata
                                        </span>
                                    )}
                                    {isDeferida && (
                                        <span
                                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-indigo-500/15 border border-indigo-500/30 text-[10px] font-medium text-indigo-400 leading-none w-fit"
                                            title={`Diferida hasta ${conv.defer_until ? new Date(conv.defer_until).toLocaleString() : ''}`}
                                        >
                                            <Clock size={9} />
                                            Diferida · {conv.defer_until ? new Date(conv.defer_until).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                        </span>
                                    )}
                                </div>
                            )}

                            {/* Archivar sigue disponible aunque la atención esté en solo lectura. */}
                            {(canManage || canArchive) && (
                                <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-slate-800/60 animate-in fade-in slide-in-from-bottom-1 duration-200">
                                    {canManage && <>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setAttachmentsConvId(conv.conversation_id); setIsAttachmentsOpen(true); }}
                                        className="p-1 rounded-md bg-slate-800 text-slate-400 hover:bg-emerald-500/20 hover:text-emerald-400 transition-colors pointer-events-auto"
                                        title="Ver Adjuntos"
                                    >
                                        <Paperclip size={12} />
                                    </button>
                                    {canTrace && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); onOpenCallTrace(conv.phone_number ? [conv.phone_number] : []); }}
                                            className="p-1 rounded-md bg-slate-800 text-slate-400 hover:bg-blue-500/20 hover:text-blue-400 transition-colors pointer-events-auto"
                                            title="Ver Historial (CallTrace)"
                                        >
                                            <History size={12} />
                                        </button>
                                    )}
                                    {availableTemplates.map(template => {
                                        const Icon = LucideIcons[template.icon_name] || MessageSquare;
                                        return (
                                            <WhatsAppTemplateButton
                                                key={template.id}
                                                templateId={template.id}
                                                title={template.name}
                                                templateName={template.name}
                                                conversationId={conv.conversation_id}
                                                phone={conv.phone_number}
                                                icon={Icon}
                                                className={'p-1 rounded-md bg-slate-800 text-slate-400 hover:bg-accent pointer-events-auto'}
                                                onError={(err) => handleTemplateError(err, template.name)}
                                                onSent={({ wa_message_id }) => {
                                                    attemptLockAndOpen(conv, false).then(() => {
                                                        try { onOpenConversation({ conversation_id: conv.conversation_id, wa_message_id }); } catch (e) { onOpenConversation(conv.conversation_id); }
                                                    });
                                                    setRefreshTrigger(prev => prev + 1);
                                                }}
                                            />
                                        );
                                    })}
                                    </>}
                                    <div className="ml-auto flex items-center gap-1.5">
                                        {/* Diferir conversación (solo agente asignado) */}
                                        {canDefer && !isDeferida && !isInmediata && deferMenuOpenFor !== conv.conversation_id && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setDeferMenuOpenFor(conv.conversation_id); }}
                                                className="p-1.5 rounded-lg transition-all text-[10px] flex items-center gap-1 pointer-events-auto bg-slate-700 text-slate-400 hover:bg-slate-600"
                                                title="Diferir conversación"
                                            >
                                                <Clock size={12} />
                                                <span className="font-medium">Diferir</span>
                                            </button>
                                        )}
                                        {canDefer && deferMenuOpenFor === conv.conversation_id && (
                                            <div className="flex items-center gap-1 pointer-events-auto" onClick={(e) => e.stopPropagation()}>
                                                {DEFER_OPTIONS.map(opt => (
                                                    <button
                                                        key={opt.minutes}
                                                        onClick={() => handleDeferConversation(conv.conversation_id, opt.minutes)}
                                                        className="px-1.5 py-1 rounded-md bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 text-[10px] font-medium"
                                                    >
                                                        {opt.label}
                                                    </button>
                                                ))}
                                                <button
                                                    onClick={() => setDeferMenuOpenFor(null)}
                                                    className="p-1 rounded-md bg-slate-800 text-slate-400 hover:bg-slate-700"
                                                    title="Cerrar"
                                                >
                                                    <X size={11} />
                                                </button>
                                            </div>
                                        )}
                                        {canDefer && (isDeferida || isInmediata) && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleDeferConversation(conv.conversation_id, null); }}
                                                className="p-1.5 rounded-lg transition-all text-[10px] flex items-center gap-1 pointer-events-auto bg-red-500/20 text-red-400 hover:bg-red-500/30"
                                                title="Cancelar diferimiento"
                                            >
                                                <AlarmClock size={12} />
                                                <span className="font-medium">Cancelar diferimiento</span>
                                            </button>
                                        )}
                                        {/* Toggle individual de en_gestion */}
                                        {conv.tramite_id && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleToggleEnGestion(conv.conversation_id, conv.en_gestion);
                                                }}
                                                className={`p-1.5 rounded-lg transition-all text-[10px] flex items-center gap-1 pointer-events-auto ${
                                                    conv.en_gestion 
                                                        ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' 
                                                        : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                                                }`}
                                                title={conv.en_gestion ? "Sacar de gestión" : "Poner en gestión"}
                                            >
                                                {conv.en_gestion ? <CheckCircle size={12} /> : <Archive size={12} />}
                                                <span className="font-medium">
                                                    {conv.en_gestion ? "En gestión" : "Archivada"}
                                                </span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Botón "Tomar Control" cuando está bloqueada por otro agente (info completa siempre visible arriba) */}
                            {isLockedByOther && !isActive && (
                                <div className="flex items-center mt-1.5 pt-1.5 border-t border-slate-800/60">
                                    <button
                                        onClick={(e) => attemptLockAndOpen(conv, true, e)}
                                        className="ml-auto px-2 py-0.5 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/20 rounded text-[10px] font-bold uppercase tracking-wider pointer-events-auto transition-colors"
                                    >
                                        Tomar Control
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                );
                })}
            </div>

            {/* Pagination Controls */}
            {!loading && total > PAGE_SIZE && (
                <div className="p-2.5 border-t border-slate-800 flex-shrink-0">
                    <div className="flex items-center justify-between gap-2">
                        <button
                            onClick={() => setPage(prev => Math.max(1, prev - 1))}
                            disabled={page === 1}
                            className="flex items-center gap-1 px-2.5 py-1.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg text-[11px] font-medium text-slate-300 transition-colors"
                            title="Página anterior"
                        >
                            <ChevronLeft size={14} />
                            <span className="hidden sm:inline">Anterior</span>
                        </button>
                        
                        <span className="text-[11px] text-slate-400 whitespace-nowrap">
                            Página <span className="font-bold text-emerald-400">{page}</span> de <span className="font-bold">{Math.ceil(total / PAGE_SIZE)}</span>
                        </span>

                        <button
                            onClick={() => setPage(prev => Math.min(Math.ceil(total / PAGE_SIZE), prev + 1))}
                            disabled={page >= Math.ceil(total / PAGE_SIZE)}
                            className="flex items-center gap-1 px-2.5 py-1.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg text-[11px] font-medium text-slate-300 transition-colors"
                            title="Página siguiente"
                        >
                            <span className="hidden sm:inline">Siguiente</span>
                            <ChevronRight size={14} />
                        </button>
                    </div>
                </div>
            )}

            {/* New Conversation Modal - Rendered via Portal to be a true global modal */}
            {isNewConvModalOpen && createPortal(
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <div 
                        className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in duration-300"
                        onClick={() => {
                            setIsNewConvModalOpen(false);
                            setNewConvPhone('');
                        }}
                    />
                    
                    {/* Modal Content Box */}
                    <div className="relative w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]" style={{ backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))', borderWidth: '1px', filter: theme === 'light' ? 'invert(1) hue-rotate(180deg)' : 'none' }}>
                        <div className="p-6 border-b flex items-center justify-between" style={{ backgroundColor: 'hsl(var(--accent))', borderBottomColor: 'hsl(var(--border))' }}>
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-emerald-500/10 rounded-xl">
                                    <Plus size={20} className="text-emerald-500" />
                                </div>
                                <h3 className="font-bold tracking-tight text-[21px]" style={{ color: 'hsl(var(--card-foreground))' }}>Nueva Conversación</h3>
                            </div>
                            <button 
                                onClick={() => {
                                    setIsNewConvModalOpen(false);
                                    setNewConvPhone('');
                                    setNewConvChannelId('');
                                }}
                                className="p-2 hover:opacity-80 rounded-full transition-colors"
                                style={{ color: 'hsl(var(--muted-foreground))' }}
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="p-8 space-y-8 overflow-y-auto custom-scrollbar">
                            {/* Channel Selector - Show if role has channels */}
                            {activeRole?.channels && activeRole.channels.length > 0 && (
                                <div className="space-y-3">
                                    <label className="text-[13px] font-bold uppercase tracking-widest pl-1" style={{ color: 'hsl(var(--muted-foreground))' }}>
                                        Canal WhatsApp
                                        <span className="text-red-500 ml-1">*</span>
                                    </label>
                                    <select
                                        value={newConvChannelId}
                                        onChange={(e) => setNewConvChannelId(e.target.value)}
                                        required
                                        className="w-full rounded-2xl px-4 py-4 text-base outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500/50 transition-all"
                                        style={{ backgroundColor: 'hsl(var(--background))', borderColor: 'hsl(var(--input))', borderWidth: '1px', color: 'hsl(var(--foreground))' }}
                                    >
                                        {activeRole.channels.length > 1 && (
                                            <option value="">-- Selecciona un canal --</option>
                                        )}
                                        {activeRole.channels.map(ch => (
                                            <option key={ch.id} value={ch.id}>
                                                {getChannelLabel(ch)}
                                            </option>
                                        ))}
                                    </select>
                                    <p className="text-[11px] text-slate-500 pl-1">Selecciona el canal desde el que se enviará la plantilla</p>
                                </div>
                            )}

                            <div className="space-y-3">
                                <label className="text-[13px] font-bold uppercase tracking-widest pl-1" style={{ color: 'hsl(var(--muted-foreground))' }}>
                                    Teléfono del Cliente
                                </label>
                                <div className="relative group">
                                    <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 group-focus-within:text-emerald-500 transition-colors" style={{ color: 'hsl(var(--muted-foreground))' }} />
                                    <input
                                        type="text"
                                        placeholder="Ej: 600111222"
                                        value={newConvPhone}
                                        onChange={(e) => setNewConvPhone(e.target.value)}
                                        className="w-full rounded-2xl pl-12 pr-24 py-4 text-xl outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500/50 transition-all font-mono"
                                        style={{ backgroundColor: 'hsl(var(--background))', borderColor: 'hsl(var(--input))', borderWidth: '1px', color: 'hsl(var(--foreground))' }}
                                        autoFocus
                                    />
                                    {newConvPhone && (
                                        <CopyablePhone
                                            phone={newConvPhone}
                                            showText={false}
                                            className="absolute right-3 top-1/2 -translate-y-1/2"
                                            buttonClassName="p-2"
                                            searchButtonClassName="p-2"
                                        />
                                    )}
                                </div>
                            </div>

                            <div className="space-y-4">
                                <label className="text-[13px] font-bold uppercase tracking-widest pl-1" style={{ color: 'hsl(var(--muted-foreground))' }}>
                                    Seleccionar Plantilla
                                </label>
                                {templatesLoading ? (
                                    <div className="flex items-center justify-center py-8">
                                        <Loader2 size={24} className="animate-spin text-emerald-500" />
                                    </div>
                                ) : availableTemplates.length === 0 ? (
                                    <div className="p-6 bg-slate-800/50 border border-slate-700 rounded-2xl text-center">
                                        <AlertCircle size={20} className="text-slate-400 mx-auto mb-2" />
                                        <p className="text-sm text-slate-400">No hay templates disponibles para tu rol</p>
                                    </div>
                                ) : (
                                    <div className="grid gap-4">
                                        {availableTemplates.map(template => {
                                            const Icon = LucideIcons[template.icon_name] || MessageSquare;
                                            return (
                                                <button
                                                    key={template.id}
                                                    onClick={() => handleStartNewConversation(template.id)}
                                                    className="w-full p-5 rounded-2xl hover:border-emerald-500/50 hover:bg-emerald-500/5 group text-left transition-all flex items-center gap-3"
                                                    style={{ backgroundColor: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))', borderColor: 'hsl(var(--border))', borderWidth: '1px' }}
                                                    title={template.description || template.name}
                                                >
                                                    <Icon size={18} className="text-emerald-500" />
                                                    <span className="text-xs font-bold uppercase tracking-widest">{template.name}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            <div className="p-5 bg-emerald-500/5 border border-emerald-500/10 rounded-2xl flex gap-4 items-start">
                                <MessageSquare className="text-emerald-500 shrink-0 mt-0.5" size={20} />
                                <p className="text-[14px] leading-relaxed italic" style={{ color: 'hsl(var(--muted-foreground))' }}>
                                    Al enviar una plantilla se creará automáticamente un nuevo **trámite** vinculado a esta conversación y se cargará en la consola.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Render del Panel de Adjuntos Desacoplado */}
            <WhatsAppAttachments
                isOpen={isAttachmentsOpen}
                attachments={waAttachments}
                waStatus={waStatus}
                onAnalyze={(att) => {
                    const conv = conversations.find(c => c.conversation_id === attachmentsConvId);
                    if (conv) handleLinkAndOpen(conv);
                    setTimeout(() => window.dispatchEvent(new CustomEvent('wa-analyze-file', { detail: att })), 300);
                }}
                onCompare={(att) => {
                    const conv = conversations.find(c => c.conversation_id === attachmentsConvId);
                    if (conv) handleLinkAndOpen(conv);
                    setTimeout(() => window.dispatchEvent(new CustomEvent('wa-compare-file', { detail: att })), 300);
                }}
                analyzedFiles={analyzedAttachments}
                extractedData={extractedData}
                onClose={() => setIsAttachmentsOpen(false)}
                onDownload={handleDownloadAttachment}
            />
        </div>
    );
};

export default WhatsAppInboxSidebar;
